import { localizarPontoNoMundo, pontosDeAncoragem, type ReferenciaPonto } from './ancoragem'
import { folgaDoTrecho } from './folgaCorda'
import type { CenaTabletop, CordaTabletop, VetorTabletop } from './tipos'

/**
 * Estudo da ancoragem básica (ponto primário com ponto secundário de reserva),
 * como na figura de referência:
 *
 *   ponto secundário ── catenária (frouxa) ── ponto primário (nó)
 *                                               └─ folga de tensão (laço frouxo)
 *   fusível (cordelete do primário à corda) ─────┘
 *                                               └─ corda até quem está pendurado
 *
 * Na mesa, é uma corda cujo percurso começa em dois pontos de ancoragem (o
 * primeiro é o secundário, o segundo o primário) e termina numa carga. O
 * fusível, se houver, é outra corda que liga o mesmo ponto do primário ao
 * terceiro ponto da corda principal (o nó do fusível); o trecho entre o
 * primário e esse nó é a folga de tensão.
 *
 * Para estudo, o app mostra o que segura a carga em cada caso e quanto quem
 * está pendurado desce e balança se um elemento falha. Conta só o comprimento
 * de corda (trechos retos mais as folgas), sem elasticidade nem força de
 * impacto: com a corda esticada de novo, a pessoa acaba na vertical do ponto
 * que sobrou. É um modelo didático, para o instrutor validar.
 */

export interface CenarioDeFalha {
  readonly id: 'uso-normal' | 'fusivel-rompe' | 'primario-falha'
  readonly titulo: string
  /** O que segura a carga neste caso. */
  readonly quemSegura: string
  /** Quanto a carga desce até parar pendurada, em metros (0 no uso normal). */
  readonly quedaM: number
  /** Distância na horizontal até a vertical do ponto que segura: o balanço depois da queda. */
  readonly balancoM: number
}

export interface AnaliseAncoragemBasica {
  readonly idCorda: string
  readonly secundario: ReferenciaPonto
  readonly primario: ReferenciaPonto
  /** O que está pendurado na ponta da corda. */
  readonly carga: ReferenciaPonto
  /** Folga da catenária, entre o secundário e o primário. */
  readonly folgaCatenariaM: number
  /** Comprimento de corda entre o secundário e o primário (reta + folga). */
  readonly comprimentoCatenariaM: number
  /** Catenária sem folga: o secundário divide a carga com o primário em vez de ficar de reserva. */
  readonly catenariaEsticada: boolean
  /** O fusível e a folga de tensão; `null` sem fusível na mesa. */
  readonly fusivel: { readonly idCorda: string; readonly no: ReferenciaPonto; readonly folgaDeTensaoM: number } | null
  readonly cenarios: readonly CenarioDeFalha[]
  /** Pontos no mundo, para os rótulos da cena. */
  readonly posicoes: {
    readonly secundario: VetorTabletop
    readonly primario: VetorTabletop
    readonly carga: VetorTabletop
    readonly noDoFusivel: VetorTabletop | null
  }
}

/** Abaixo desta folga a catenária conta como esticada. */
export const FOLGA_MINIMA_CATENARIA_M = 0.05

const mesmoPonto = (a: ReferenciaPonto, b: ReferenciaPonto) => a.idObjeto === b.idObjeto && a.idPonto === b.idPonto

function usoDoPonto(cena: CenaTabletop, ref: ReferenciaPonto) {
  const objeto = cena.objetos.find((o) => o.id === ref.idObjeto)
  return objeto ? pontosDeAncoragem(objeto).find((p) => p.id === ref.idPonto)?.uso : undefined
}

const distancia = (a: VetorTabletop, b: VetorTabletop) => Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2])
const horizontal = (a: VetorTabletop, b: VetorTabletop) => Math.hypot(b[0] - a[0], b[2] - a[2])

/** Comprimento de corda do trecho `i` (reta mais folga), com os pontos já no mundo. */
const comprimentoDoTrecho = (corda: CordaTabletop, pontos: readonly VetorTabletop[], i: number) =>
  distancia(pontos[i]!, pontos[i + 1]!) + folgaDoTrecho(corda, i)

function analisarCorda(cena: CenaTabletop, corda: CordaTabletop): AnaliseAncoragemBasica | null {
  const { percurso } = corda
  if (percurso.length < 3 || corda.funcao === 'talabarte') return null
  const [secundario, primario] = [percurso[0]!, percurso[1]!]
  const carga = percurso[percurso.length - 1]!
  if (usoDoPonto(cena, secundario) !== 'ancoragem' || usoDoPonto(cena, primario) !== 'ancoragem' || usoDoPonto(cena, carga) !== 'carga') return null
  if (secundario.idObjeto === primario.idObjeto) return null
  const pontos = percurso.map((r) => localizarPontoNoMundo(cena, r))
  if (pontos.some((p) => p === null)) return null
  const p = pontos as VetorTabletop[]

  // o fusível: outra corda do mesmo ponto do primário ao terceiro ponto da corda (que não é a carga)
  const no = percurso.length > 3 ? percurso[2]! : null
  const cordaFusivel = no
    ? (cena.cordas ?? []).find(
        (c) =>
          c.id !== corda.id &&
          c.percurso.length === 2 &&
          c.percurso.some((r) => mesmoPonto(r, primario)) &&
          c.percurso.some((r) => r.idObjeto === no.idObjeto),
      )
    : undefined

  const comprimentos = p.slice(1).map((_, i) => comprimentoDoTrecho(corda, p, i))
  const catenaria = comprimentos[0]!
  const doPrimarioAteACarga = comprimentos.slice(1).reduce((s, c) => s + c, 0)
  const pCarga = p[p.length - 1]!
  const [pSecundario, pPrimario] = [p[0]!, p[1]!]

  const cenarios: CenarioDeFalha[] = [
    {
      id: 'uso-normal',
      titulo: 'Uso normal',
      quemSegura: cordaFusivel
        ? 'O fusível, preso ao ponto primário. A folga de tensão e a catenária ficam frouxas, de reserva.'
        : 'O nó no ponto primário. A catenária fica frouxa, de reserva.',
      quedaM: 0,
      balancoM: 0,
    },
  ]
  if (cordaFusivel) {
    cenarios.push({
      id: 'fusivel-rompe',
      titulo: 'O fusível rompe',
      quemSegura: 'A folga de tensão estica e o nó no ponto primário assume a carga.',
      quedaM: Math.max(0, pCarga[1] - (pPrimario[1] - doPrimarioAteACarga)),
      balancoM: horizontal(pCarga, pPrimario),
    })
  }
  cenarios.push({
    id: 'primario-falha',
    titulo: 'O ponto primário falha',
    quemSegura: 'A catenária estica e o ponto secundário assume a carga.',
    quedaM: Math.max(0, pCarga[1] - (pSecundario[1] - catenaria - doPrimarioAteACarga)),
    balancoM: horizontal(pCarga, pSecundario),
  })

  return {
    idCorda: corda.id,
    secundario,
    primario,
    carga,
    folgaCatenariaM: folgaDoTrecho(corda, 0),
    comprimentoCatenariaM: catenaria,
    catenariaEsticada: folgaDoTrecho(corda, 0) < FOLGA_MINIMA_CATENARIA_M,
    fusivel: cordaFusivel && no ? { idCorda: cordaFusivel.id, no, folgaDeTensaoM: folgaDoTrecho(corda, 1) } : null,
    cenarios,
    posicoes: { secundario: pSecundario, primario: pPrimario, carga: pCarga, noDoFusivel: cordaFusivel ? p[2]! : null },
  }
}

/** As ancoragens básicas da mesa: uma por corda que sai de um secundário, passa por um primário e chega a uma carga. */
export function analisarAncoragensBasicas(cena: CenaTabletop): readonly AnaliseAncoragemBasica[] {
  return (cena.cordas ?? []).flatMap((c) => analisarCorda(cena, c) ?? [])
}
